import axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import { MessageConsumer } from '../../../contexts/MessageContext';
import Button from '../../Input/Button/Button';
import InputField from '../../Input/InputField/InputField';
import { Consumer as PageConsumer } from '../../../contexts/PageContext';
import './ContactSearch.scss';
import { PAGE_DISPLAY_MODE } from '../../../pages/constants';
import UserImage from '../../Content/User/UserDisplay/UserImage/UserImage';

export class ContactSearch extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            searchTerm: '',
            contactList: [],
            isInputActive: props.location === 'NETWORK',
            noContactMatchFound: false,
            shouldShowDropdown: false,
        };
        this.searchInputRef = React.createRef();
    }

    static propTypes = {
        /**
         * whether message box is active or not.
         * if it's active, the message box will cover up the entire page, otherwise it will be hidden
         * the styling only effect if the media queries in mobile size
         */
        isMessageBoxActive: PropTypes.bool,
        /**
         * whether the component is hidden in desktop view or not
         */
        hiddenInDesktopWidth: PropTypes.bool,
        /**
         * function inherit from MessageContext that trigger when a contact is selected from dropdown
         */
        createChannel: PropTypes.func.isRequired,
        /**
         * function that toggles on messages box's active state
         */
        toggleMessageBoxActive: PropTypes.func.isRequired,
        /**
         * string enum of MESSAGING | NETWORK
         */
        location: PropTypes.oneOf(['MESSAGING', 'NETWORK']),
        /**
         * string custom css class name
         */
        className: PropTypes.string,
        /**
         * string placeholder for InputField
         */
        inputPlaceholder: PropTypes.string,
        /**
         * function to set search results in parent component
         */
        setSearchResults: PropTypes.func,
        /**
         * array of search results passed down
         */
        searchResults: PropTypes.array,
        /**
         * function to call api from parent
         */
        fetchSearchMatchContact: PropTypes.func,
    };

    static defaultProps = {
        hiddenInDesktopWidth: false,
        isMessageBoxActive: false,
        createChannel: () => {},
        toggleMessageBoxActive: () => {},
        location: 'MESSAGING',
        className: '',
        inputPlaceholder: 'Search for contacts',
        setSearchResults: () => {},
    };

    startInputFocus = () => {
        this.searchInputRef.current.focus();
    };

    removeInputFocus = () => {
        this.searchInputRef.current.blur();
        setTimeout(() => {
            this.setState({
                noContactMatchFound: false,
                isInputActive: false,
                contactList: [],
                shouldShowDropdown: false,
            });
        }, 300);
    };

    fetchSearchMatchContact = async (searchTerm = '') => {
        if (this.props.location === 'NETWORK') {
            const searchPayload = {
                type: 'search',
                apiKey: 'connectionSearch',
                searchTerm,
            };

            try {
                const results = await this.props.fetchSearchMatchContact(
                    10,
                    1,
                    searchPayload
                );
                return results;
            } catch (e) {
                console.error(e);
                return;
            }
        }

        try {
            this._source = axios.CancelToken.source();
            const res = await axios.get(
                `/api/user/connection/typeahead/${searchTerm}`,
                { cancelToken: this._source.token }
            );
            const matchedContactList = res.data;
            return matchedContactList;
        } catch (error) {
            return;
        }
    };

    setSearchResults = (results) => {
        const contactList = results || [];
        const restructuredResults = contactList.map((user) => ({ user }));

        this.props.setSearchResults(restructuredResults);
    };

    handleSearchInputChange = async (searchTerm = '') => {
        if (!searchTerm) {
            this.setState(
                {
                    noContactMatchFound: false,
                    contactList: [],
                    searchTerm: '',
                },
                this.setSearchResults
            );
            return;
        }

        this.setState(
            {
                searchTerm,
            },
            async () => {
                try {
                    const matchedContactList = await this.fetchSearchMatchContact(
                        this.state.searchTerm
                    );
                    const isResultNull =
                        !Array.isArray(matchedContactList) ||
                        !matchedContactList.length;
                    this.setState({
                        contactList: isResultNull ? [] : matchedContactList,
                        noContactMatchFound: isResultNull,
                        shouldShowDropdown: true,
                    });
                } catch (error) {
                    return;
                }
            }
        );
    };

    handleStartButtonClick = () => {
        this.startInputFocus();
        this.setState({
            isInputActive: true,
        });
    };

    handleSelectContact = (contact) => async () => {
        const { userId: contactUserId } = contact;
        const { createChannel, toggleMessageBoxActive } = this.props;
        const isMessagingPage = this.props.location === 'MESSAGING';

        if (contactUserId && isMessagingPage) {
            try {
                await createChannel('community-user-message', {
                    userId: contactUserId,
                });
                toggleMessageBoxActive();
                this.removeInputFocus();
                this.setState({
                    searchTerm: '',
                    contactList: [],
                });
                return;
            } catch (error) {
                return;
            }
        }

        this.setState(
            {
                contactList: [],
            },
            () => this.setSearchResults([contact])
        );
    };

    render() {
        const {
            contactList,
            isInputActive,
            noContactMatchFound,
            searchTerm,
        } = this.state;
        const {
            isMessageBoxActive,
            hiddenInDesktopWidth,
            location,
        } = this.props;

        const isNetworkPage = location === 'NETWORK';
        const isMessagingPage = location === 'MESSAGING';

        const searchResultsList = isNetworkPage
            ? this.props.searchResults.map(({ user }) => user)
            : contactList;
        return (
            <div
                className={`
                    component-Messages-ContactSearch
                    ${
                        isMessageBoxActive
                            ? 'component-Messages-ContactSearch-hidden'
                            : ''
                    }
                    ${
                        hiddenInDesktopWidth
                            ? 'component-Messages-ContactSearch-desktop-hidden'
                            : ''
                    }
                    ${this.props.className}
                `}
                onClick={isInputActive ? () => {} : this.handleStartButtonClick}
            >
                {isMessagingPage && (
                    <Button
                        type="solid"
                        value="Start a conversation"
                        class={`start-btn-wrapper`}
                        inputStyle={`
                        start-btn-shown
                        ${isInputActive ? 'start-btn-hidden' : ''}
                    `}
                        onClick={
                            isInputActive
                                ? () => {}
                                : this.handleStartButtonClick
                        }
                    />
                )}
                <InputField
                    name={'contact list typeahead'}
                    onChange={this.handleSearchInputChange}
                    onBlur={this.removeInputFocus}
                    handleInput={this.searchInputRef}
                    placeholder={this.props.inputPlaceholder}
                    autocomplete={'off'}
                    inputValue={searchTerm}
                    icon={isNetworkPage ? 'magnifying-glass' : null}
                    classname={`
                        ${isMessagingPage ? 'contact-search-input-hidden' : ''}
                        ${isInputActive ? 'contact-search-input-shown' : ''}
                    `}
                />
                {this.state.shouldShowDropdown && (
                    <div className="contact-list-dropdown-wrapper">
                        <div className="contact-list-dropdown">
                            {noContactMatchFound && (
                                <div className="contact-list-item contact-list-item-no-connection">
                                    <p className="desc">
                                        No connections match this search.
                                    </p>
                                </div>
                            )}
                            {searchResultsList.map((contact, index) => (
                                <div
                                    data-test={`connected-user-${index}`}
                                    className="contact-list-item"
                                    key={`contact-list-item-${index}`}
                                    onClick={this.handleSelectContact(contact)}
                                >
                                    <UserImage
                                        className={'img'}
                                        image={contact.image}
                                        iconSize={22}
                                    />
                                    <p className="username">
                                        {contact.username}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default (props) => (
    <PageConsumer>
        {({ pageProps: { pageDisplayMode } }) => (
            <MessageConsumer>
                {({ createChannel }) =>
                    pageDisplayMode !== PAGE_DISPLAY_MODE.EMPLOYER_DEFAULT ? (
                        <ContactSearch
                            {...props}
                            createChannel={createChannel}
                        />
                    ) : null
                }
            </MessageConsumer>
        )}
    </PageConsumer>
);
